/**
 * First-session bridge (ADR-0020, LK-3).
 *
 * The story is the only path that installs apps, but a new player's desktop
 * only has Chat and Terminal. Open Chat once and the story starts: the server
 * applies the `act1_bronx_open` install beats (Market + Expediente) and we sync
 * the authoritative `installedApps` back into the client.
 *
 * The trigger is keyed by playerId in memory AND in localStorage, so a page
 * reload does not start the story again or re-add the opening line (LK-54-04).
 * A genuinely new player gets the opening line once; a returning player gets a
 * single resumed line instead. If the story is already running (e.g. the
 * Journal started it) the server returns the current node and the same sync
 * happens; nothing restarts.
 */
import useCoreSession from '../stores/coreSession';
import useGameState from './gameState';
import {
  announceNewInstalls,
  getInstalledApps,
  installApp,
} from './installedApps';

// Vinnie's first line mirrors the `act1_bronx_open` story text: he sent
// MARKET.APP (and EXPEDIENTE) and the debt is his to collect.
export const FIRST_CHAT_STORY_LINE =
  'I sent you MARKET.APP and EXPEDIENTE. Check your desktop. The debt is mine to collect; walk to my register before the meter gets creative.';

// Returning player: the story already started in a previous page session.
export const RESUMED_CHAT_STORY_LINE =
  'LINE RESTORED // THE RECORD IS STILL OPEN.';

// Offline / start failure: the local shell installed both apps, but the
// server-side story did not start, so do not claim it did (LK-54-03).
export const OFFLINE_CHAT_STORY_LINE =
  'LINE DOWN // LOCAL SHELL. MARKET.APP AND EXPEDIENTE ARE ON YOUR DESKTOP; THE REGISTER IS STILL WAITING.';

const STORAGE_PREFIX = 'dcw_first_story_started:';

let startedForPlayer = null;
let resumedNotified = false;
let offlineFallbackDone = false;
let inFlight = null;

function readStorage(key) {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage can be unavailable in private mode */
  }
}

/** True when this player already started the story in an earlier page load. */
export function hasStartedFirstStory(playerId) {
  if (!playerId) return false;
  return readStorage(STORAGE_PREFIX + playerId) === '1';
}

function markFirstStoryStarted(playerId) {
  if (!playerId) return;
  writeStorage(STORAGE_PREFIX + playerId, '1');
}

export function currentStoryPlayerId() {
  const core = useGameState.getState().core || {};
  return useCoreSession.getState().playerId || core.playerId || null;
}

/**
 * Start the story on the first Chat open, once per player (survives reload).
 *
 * Returns a promise with `{ started, resumed?, fallback?, offline?, playerId }`
 * for the caller that actually performed the work, or `null` when the trigger
 * is a duplicate or the identity is not ready yet. `onStarted` is invoked once
 * only for the owning caller, so Chat adds exactly one opening/resumed line.
 */
export function ensureFirstChatStory({ onStarted } = {}) {
  const playerId = currentStoryPlayerId();
  if (playerId && startedForPlayer === playerId) return Promise.resolve(null);
  if (inFlight) return inFlight;

  const notify = typeof onStarted === 'function' ? onStarted : null;

  // No identity yet: wait for the DopeOS boot unless the line is already down.
  if (!playerId) {
    if (useCoreSession.getState().status !== 'offline') return Promise.resolve(null);
    if (offlineFallbackDone) return Promise.resolve(null);
    offlineFallbackDone = true;
    // The opening beat promises both apps, so the offline shell installs both
    // locally instead of only Market (LK-54-03).
    installApp('market', { from: 'vinnie' });
    installApp('journal', { from: 'vinnie' });
    const outcome = { started: false, fallback: true, offline: true, playerId: null };
    if (notify) notify(outcome);
    return Promise.resolve(outcome);
  }

  // Already started before this page load: never call startStory again and
  // never re-add the opening line (LK-54-04).
  if (hasStartedFirstStory(playerId)) {
    startedForPlayer = playerId;
    if (resumedNotified) return Promise.resolve(null);
    resumedNotified = true;
    const outcome = { started: false, resumed: true, playerId };
    if (notify) notify(outcome);
    return Promise.resolve(outcome);
  }

  inFlight = (async () => {
    try {
      let result;
      try {
        result = await useCoreSession.getState().startStory();
      } catch (error) {
        // Offline/unreachable: keep the first beat playable with local
        // installs. Not persisted, so a later reload can try the server again.
        const installed = [
          installApp('market', { from: 'vinnie' }),
          installApp('journal', { from: 'vinnie' }),
        ].some(Boolean);
        startedForPlayer = playerId;
        const outcome = { started: false, fallback: true, playerId, error, installed };
        if (notify) notify(outcome);
        return outcome;
      }

      // The story is running now; never call startStory() again for this
      // player even if the state sync below fails.
      startedForPlayer = playerId;
      markFirstStoryStarted(playerId);

      let syncError = null;
      const previousApps = getInstalledApps();
      try {
        const storyState = result?.state;
        if (storyState && Array.isArray(storyState.installedApps)) {
          useCoreSession.getState().applyState(storyState);
        } else {
          // `/story/start` returns story state, not player state; read the
          // authoritative `installedApps` from GET /core/state.
          await useCoreSession.getState().refreshState();
        }
        // ADR-0020: server-installed apps leave a desktop notification too.
        announceNewInstalls(previousApps, {
          from: result?.story?.node?.speaker || 'vinnie',
        });
      } catch (error) {
        syncError = error;
      }

      const outcome = { started: true, playerId, result, syncError };
      if (notify) notify(outcome);
      return outcome;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

export function __resetFirstChatStoryForTests() {
  startedForPlayer = null;
  resumedNotified = false;
  offlineFallbackDone = false;
  inFlight = null;
}

export default ensureFirstChatStory;
